// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import {
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonList
} from "@ionic/react"
import { useState } from "react"
import { useLocales } from "react-localized"
import { SingleValue } from 'react-select'
import AsyncSelect from 'react-select/async'
import { PurchaseOrderProductDTO } from "../models"
import AppleProducts from './apple.json'
import { useContextMenu } from "../lib"


interface OrderProductEditComponentProps {
  currency: string
  item?: PurchaseOrderProductDTO
  onUpdate(dto: PurchaseOrderProductDTO): void
  onDelete?: () => void
}


const OrderProductEditComponent: React.FC<OrderProductEditComponentProps> = ({
  currency,
  item,
  onDelete,
  onUpdate
}) => {
  const {gettext} = useLocales()
  const [params, setParams] = useState<any>({
    quantity: 0,
    price: "0.00",
    ...(item || {})
  })
  const moneyFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency
  })
  const productList: {label: string, value: string}[] = AppleProducts.map((product) => {
    return {label: product.name, value: product.sku}
  })
  const isValid: () => boolean = () => {
    return !!params.name
      && !!params.quantity
      && !!params.price
      && (!isNaN(parseFloat(params.price || "")))
  }
  const updateButtonText: string = (!!item)
    ? gettext("Update item details")
    : gettext("Add product to order")
  return (
    <>
      <AsyncSelect
        classNames={{
          container: (state) => "select-container",
          control: (state) => "control",
          indicatorsContainer: (state) => "indicators",
          menuList: (state) => "menu-list",
          menu: (state) => "menu",
          option: (state) => (!!state.isFocused) ? "focus" : "",
          placeholder: (state) => "placeholder",
          singleValue: (state) => "current-value",
          valueContainer: (state) => "value-container"
        }}
        defaultOptions
        onChange={(option: SingleValue<any>) => {
          setParams({
            ...params,
            sku: option.value,
            name: option.label
          })
        }}
        loadOptions={async (v: string) => {
          return productList.filter((product) => {
            return product.label.toLowerCase().includes(`iphone ${v}`) ||  v === ''
          })
        }}
        value={null}
      />
      <IonList>
        <IonItem>
          <IonLabel position="stacked">{gettext("SKU")}</IonLabel>
          <IonInput
            readonly
            type="text"
            value={params.sku || ""}
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">{gettext("Product name")}</IonLabel>
          <IonInput
            minlength={3}
            onIonChange={(event) => {
              setParams({...params, name: event.target.value})
            }}
            type="text"
            value={params.name || ""}
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">{gettext("Grade")}</IonLabel>
          <IonInput
            minlength={1}
            onIonChange={(event) => {
              setParams({...params, grade: event.target.value})
            }}
            type="text"
            value={params.grade || ""}
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">{gettext("Quantity")}</IonLabel>
          <IonInput
            onIonChange={(event) => {
              let value = (typeof event.target.value === 'number')
                ? event.target.value.toString()
                : event.target.value || "0"
              setParams({...params, quantity: parseInt(value)})
            }}
            type="number"
            value={params.quantity || 0}
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">{gettext`Price (${currency})`}</IonLabel>
          <IonInput
            onIonChange={(event) => {
              let value = (typeof event.target.value === 'number')
                ? event.target.value.toString()
                : event.target.value || ""
              setParams({...params, price: value})
            }}
            type="number"
            value={params.price || ""}
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">{gettext`Total`}</IonLabel>
          <IonInput
            readonly
            type="text"
            value={moneyFormatter.format(parseInt(params.quantity) * parseFloat(params.price || "0"))}
          />
        </IonItem>
      </IonList>
      <IonButton
        disabled={!isValid()}
        onClick={() => {
          onUpdate(params)
          if (!item) {
            setParams({price: "0.00", quantity: 0})
          }
        }}
        style={{width: "100%"}}
      >
        {updateButtonText}
      </IonButton>
      {(onDelete === undefined) ? null : (
        <IonButton
          color="danger"
          fill="clear"
          onClick={() => {
            onDelete()
          }}
          style={{width: "100%"}}
        >
          {gettext("Remove from order")}
        </IonButton>
      )}
    </>
  ) 
}

export {OrderProductEditComponent}